import { ChangeEvent, useState } from 'react';

interface EntityLogoProps {
  /** ID de l'entité (company ou institution) */
  entityId: string;
  /** URL du logo actuel */
  logo?: string;
  /** Nom de l'entité (pour les initiales) */
  name: string;
  /** Fonction d'upload du logo - doit retourner l'URL ou null */
  onUpload: (file: File) => Promise<string | null>;
  /** Callback appelé après mise à jour du logo */
  onLogoUpdate?: (success: boolean, newUrl?: string) => void;
  /** Indique si un upload est en cours */
  isUploading?: boolean;
}

/**
 * Composant générique pour afficher et modifier le logo d'une entité (Company ou Financial Institution)
 * 
 * Contrairement à CompanyLogo qui utilise useCompany en interne, ce composant
 * reçoit la fonction d'upload en prop, ce qui le rend réutilisable pour les deux types d'entités.
 */
export function EntityLogo({ 
  entityId, 
  logo, 
  name, 
  onUpload, 
  onLogoUpdate,
  isUploading = false 
}: EntityLogoProps) {
  const [isHovering, setIsHovering] = useState(false);
  const [isLocalUploading, setIsLocalUploading] = useState(false);
  
  const isUpdating = isUploading || isLocalUploading;
  
  const initials = name
    .split(' ')
    .filter(word => word.length > 0)
    .map(word => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase() || 'IF';

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Vérifier le type et la taille du fichier
    if (!file.type.includes('image/')) {
      alert('Veuillez sélectionner une image.');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) { // 5MB max
      alert("L'image est trop volumineuse. La taille maximale est de 5MB.");
      return;
    }
    
    setIsLocalUploading(true);
    try {
      const newUrl = await onUpload(file);
      if (onLogoUpdate) {
        onLogoUpdate(!!newUrl, newUrl || undefined);
      }
    } finally {
      setIsLocalUploading(false);
    }
  };

  return (
    <div className="relative group">
      <div 
        className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden relative border-2 border-gray-200 shadow-sm"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {logo ? (
          <img
            src={logo}
            alt={`Logo de ${name}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-primary-100 text-primary-700 text-2xl font-bold">
            {initials}
          </div>
        )}
        
        <div
          className={`absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center transition-opacity duration-200 ${
            isHovering ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <label
            htmlFor={`logo-upload-${entityId}`}
            className="text-white cursor-pointer px-2 py-1 text-xs sm:text-sm rounded-md hover:underline"
          >
            {isUpdating ? 'Chargement...' : 'Modifier'}
          </label>
          <input
            id={`logo-upload-${entityId}`}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
            disabled={isUpdating}
          />
        </div>
      </div>
      
      {isUpdating && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
        </div>
      )}
    </div>
  );
}
